import React, { SyntheticEvent, useCallback, useRef, useState } from 'react';

import { useTranslation } from '../../i18n';

import { Form, Input, Select, SelectOption, Button } from '../element';
import { TextArea } from '../textarea';

import { getFeedBackRules } from './rules';

const initialState = Object.freeze({
  question: '',
  name: '',
  email: '',
  text: '',
});

export interface FeedBackFormProps {
  handleSubmit(model: Record<string, string>): void;
}

export const FeedBackForm: React.FC<FeedBackFormProps> = ({ handleSubmit }) => {
  const [t] = useTranslation(['contacts', 'common']);
  const [state, setState] = useState(initialState);

  const list = t('contacts.form.questions', { returnObjects: true });
  const questions: string[] = Array.isArray(list) ? list : [];

  const onChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string) => (value: any) =>
      setState((prev) => ({
        ...prev,
        [key]: value,
      })),
    []
  );

  const ref = useRef<InstanceType<typeof Form>>(null);

  const onSubmit = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();

      if (ref.current) {
        ref.current.validate((isValid) => {
          if (isValid) {
            handleSubmit.call(null, { ...state });

            if (ref.current) {
              ref.current.resetFields();
              setState({ ...initialState });
            }
          }
        });
      }
    },
    [state]
  );

  return (
    <Form ref={ref} model={state} rules={getFeedBackRules(t)} {...{ onSubmit }} labelPosition="top" labelWidth="80px">
      <Form.Item label={t('contacts.form.subject')} prop="question">
        <Select
          value={state.question}
          placeholder={t('contacts.form.subject')}
          style={{ width: '100%' }}
          onChange={onChange('question')}
        >
          {questions.map((value) => (
            <SelectOption key={value} label={value} value={value} />
          ))}
        </Select>
      </Form.Item>
      <Form.Item label={t('contacts.form.name')} prop="name">
        <Input value={state.name} placeholder={t('contacts.form.name')} onChange={onChange('name')} />
      </Form.Item>
      <Form.Item label={t('contacts.form.email')} prop="email">
        <Input value={state.email} placeholder={t('contacts.form.email')} onChange={onChange('email')} />
      </Form.Item>
      <Form.Item label={t('contacts.form.message')} prop="text">
        <TextArea
          wordLimit
          maxLength={255}
          value={state.text}
          placeholder={t('contacts.form.message')}
          autosize={{ minRows: 4 }}
          onChange={onChange('text')}
        />
      </Form.Item>

      <Button type="primary" nativeType="submit">
        {t('contacts.form.submit')}
      </Button>
    </Form>
  );
};
